import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify";

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
  name: string;
  size: number;
  uploadDate: Date;
  category?: string;
  tags?: string[];
  altText?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StorageUsage {
  totalStorageGB: number;
  usedStorageGB: number;
  remainingStorageGB: number;
  usagePercentage: number;
  spaceAvailable: boolean;
  mediaCount: number;
  imageCount: number;
  videoCount: number;
  totalSizeBytes: number;
}

/**
 * Service for handling file uploads to S3
 */
export const uploadService = {
  /**
   * Get S3 storage usage for media files
   * @param token - JWT token for authentication
   * @returns Storage usage information
   */
  getStorageUsage: async (token: string): Promise<StorageUsage> => {
    try {
      const url = `${awsConfig.apiEndpoint}/media/storage`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch storage usage");
      }

      return await response.json();
    } catch (error: any) {
      // Return a default object with 0 values
      return {
        totalStorageGB: 5,
        usedStorageGB: 0,
        remainingStorageGB: 5,
        usagePercentage: 0,
        spaceAvailable: true,
        mediaCount: 0,
        imageCount: 0,
        videoCount: 0,
        totalSizeBytes: 0
      };
    }
  },

  /**
   * Get a pre-signed URL for uploading a file to S3
   * @param fileName - The name of the file
   * @param contentType - The MIME type of the file
   * @param fileSize - The size of the file in bytes
   * @param token - JWT token for authentication
   * @returns Object containing upload URL and public URL
   */
  getPresignedUrl: async (
    fileName: string,
    contentType: string,
    fileSize: number,
    token: string
  ): Promise<{ uploadUrl: string; publicUrl: string; mediaType?: string; key?: string }> => {
    const url = `${awsConfig.apiEndpoint}/uploads/url`;
    
    try {
      //console.log("Making request to:", url);
      //console.log("Token available:", !!token);
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        },
        body: JSON.stringify({ fileName, contentType, fileSize }),
      });
   
      if (!response.ok) {
        const errorData = await response.json().catch(() => 
          ({ message: `HTTP error ${response.status}: ${response.statusText}` })
        );
      
        throw new Error(errorData.message || `Failed to get upload URL: HTTP ${response.status}`);
      }

      const data = await response.json();
      //console.log("Pre-signed URL response:", data);
      return {
        uploadUrl: data.uploadUrl,
        publicUrl: data.publicUrl,
        mediaType: data.mediaType,
        key: data.key
      };
    } catch (error: any) {
      console.error("Error getting upload URL:", error);
      toast.error(error.message || "Failed to get upload URL");
      throw error;
    }
  },

  /**
   * Upload a file using a pre-signed URL
   * @param uploadUrl - The pre-signed URL for the upload
   * @param file - The file to upload
   * @returns Public URL for the uploaded file
   */
  uploadFile: async (
    uploadUrl: string,
    file: File,
    publicUrl: string
  ): Promise<string> => {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      return publicUrl;
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast.error(error.message || "Failed to upload file");
      throw error;
    }
  },
  
  /**
   * Upload a file to S3 using a pre-signed URL
   * @param file - The file to upload
   * @param token - JWT token for authentication
   * @param prefix - Optional prefix for the file name
   * @returns URL of the uploaded file
   */
  upload: async (file: File, token: string, prefix = "resources/"): Promise<string> => {
    try {
      // Check storage usage first
      const storageUsage = await uploadService.getStorageUsage(token);
      
      if (!storageUsage.spaceAvailable) {
        throw new Error(`Storage limit reached (5GB). Please delete some files before uploading.`);
      }
      
      // Check if this specific file would exceed the limit
      if (file.size + storageUsage.totalSizeBytes > 5 * 1024 * 1024 * 1024) {
        throw new Error(`This file would exceed the 5GB storage limit. Current usage: ${storageUsage.usedStorageGB.toFixed(2)}GB, File size: ${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`);
      }
      
      // Generate a unique filename to avoid conflicts
      const uniqueFileName = `${prefix}${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      
      // Get pre-signed URL with file size
      const { uploadUrl, publicUrl, mediaType, key } = await uploadService.getPresignedUrl(
        uniqueFileName,
        file.type,
        file.size,
        token
      );
      
      // Upload the file
      await uploadService.uploadFile(uploadUrl, file, publicUrl);
      
      // Save media item to database
      try {
        const mediaData = {
          url: publicUrl,
          type: mediaType || (file.type.startsWith('image/') ? 'image' : 'video'),
          name: file.name,
          size: file.size,
          key: key
        };
        
        const url = `${awsConfig.apiEndpoint}/media`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(mediaData),
        });
        
        if (!response.ok) {
          console.error("Failed to save media item to database");
          // Continue anyway to return the URL
        }
      } catch (dbError) {
        console.error("Error saving media item:", dbError);
        // Continue anyway to return the URL
      }
      
      //console.log("File uploaded. Access URL:", publicUrl);
      return publicUrl;
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast.error(error.message || "Failed to upload file");
      throw error;
    }
  },

  /**
   * Get all media files
   * @param token - JWT token for authentication
   * @returns Array of media items
   */
  getAllMedia: async (token: string): Promise<MediaItem[]> => {
    try {
      const url = `${awsConfig.apiEndpoint}/media`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch media files");
      }

      const data = await response.json();
      return data.map((item: any) => ({
        ...item,
        uploadDate: new Date(item.createdAt || Date.now())
      }));
    } catch (error: any) {
      console.error("Error fetching media files:", error);
      // Don't show a toast here as it would appear on every page load
      // Just return empty array instead
      return [];
    }
  },

  /**
   * Delete a media file
   * @param mediaId - ID of the media file to delete
   * @param token - JWT token for authentication
   * @returns Success status
   */
  deleteMedia: async (mediaId: string, token: string): Promise<boolean> => {
    try {
      const url = `${awsConfig.apiEndpoint}/media/${mediaId}`;
      
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete media file");
      }

      return true;
    } catch (error: any) {
      console.error("Error deleting media file:", error);
      toast.error(error.message || "Failed to delete media file");
      throw error;
    }
  },

  /**
   * Update media file metadata
   * @param mediaId - ID of the media file to update
   * @param metadata - Metadata to update
   * @param token - JWT token for authentication
   * @returns Updated media item
   */
  updateMediaMetadata: async (
    mediaId: string, 
    metadata: Partial<MediaItem>, 
    token: string
  ): Promise<MediaItem> => {
    try {
      const url = `${awsConfig.apiEndpoint}/media/${mediaId}`;
      
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(metadata),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update media metadata");
      }

      const data = await response.json();
      return {
        ...data,
        uploadDate: new Date(data.createdAt || Date.now())
      };
    } catch (error: any) {
      console.error("Error updating media metadata:", error);
      toast.error(error.message || "Failed to update media metadata");
      throw error;
    }
  },

  /**
   * Get a public URL for a media file
   * @param mediaId - ID of the media file
   * @returns Public URL for the media file
   */
  getPublicMediaUrl: async (mediaId: string): Promise<string> => {
    try {
      const url = `${awsConfig.apiEndpoint}/media/file/${mediaId}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        }
      });

      if (!response.ok) {
        // If there's an error, fall back to direct URL
        console.error("Error getting public media URL, falling back to direct URL");
        return "";
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error("Error getting public media URL:", error);
      return "";
    }
  },

  /**
   * Refresh the URL for a media item
   * This is particularly useful for videos which may have pre-signed URLs that expire
   * @param mediaId - ID of the media to refresh
   * @returns A refreshed media item with updated URLs
   */
  refreshMediaUrl: async (mediaId: string, token: string): Promise<MediaItem | null> => {
    try {
      // First try the public endpoint
      const publicUrl = await uploadService.getPublicMediaUrl(mediaId);
      
      if (publicUrl) {
        // If we successfully got a public URL
        const response = await uploadService.updateMediaMetadata(
          mediaId,
          { url: publicUrl },
          token
        );
        
        return response;
      }
      
      // If public URL failed, try to get from the admin API
      const url = `${awsConfig.apiEndpoint}/media/${mediaId}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh media URL");
      }

      const data = await response.json();
      return {
        ...data,
        uploadDate: new Date(data.createdAt || Date.now())
      };
    } catch (error: any) {
      console.error("Error refreshing media URL:", error);
      return null;
    }
  },
}; 